import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import CustomSearchbar from 'components/CustomSearchBar';
import DeviceList from 'components/DeviceComponents/DeviceList';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import ErrorText from 'components/ErrorText';
import CustomTrashIcon from 'components/Icons/CustomTrashIcon';

import { useAppDispatch, useAppSelector } from 'redux-store';
import deleteDeviceMemberAccessThunk from 'redux-store/thunks/organizationOverview/deleteDeviceMemberAccessThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AssignDevicesToMembersModalStageProps } from '..';

const ManageDevices: FC<AssignDevicesToMembersModalStageProps> = ({
  state,
  setState,
  organizationId,
  userId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const fullStatus = useAppSelector(appState => appState.api.fullStatus);

  const organizationMembers = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.users ?? [],
  );

  const organizationDevices = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.devices ?? [],
  );

  const devicesAssignedToMember = useMemo(
    () => organizationMembers.find(org => org.id === userId)?.devices || [],
    [organizationMembers, userId],
  );

  const userRole = useMemo(
    () => organizationMembers.find(member => member.id === userId)?.role,
    [organizationMembers, userId],
  );

  const currentAssignedDevices = organizationDevices.filter(device =>
    devicesAssignedToMember.includes(device.id),
  );

  const devicesWithNames = useMemo(() => {
    return currentAssignedDevices.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [currentAssignedDevices, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  useEffect(() => {
    const newAddedDevice = currentAssignedDevices.find(
      device => device.serial === state.newDeviceSerial,
    );

    if (newAddedDevice?.id) {
      setState(prev => ({ ...prev, devices: [newAddedDevice.id] }));
    }
  }, [currentAssignedDevices, setState, state.newDeviceSerial]);

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  const handleUnassignDeviceFromMember = useCallback(
    async (deviceId: number) => {
      if (!organizationId || !userId) return;

      try {
        setLoading(true);

        const result = await dispatch(
          deleteDeviceMemberAccessThunk({
            organizationId: organizationId,
            data: {
              user_assignments: [
                {
                  user_id: userId,
                  device_ids: [deviceId],
                },
              ],
            },
          }),
        ).unwrap();

        if (result.success) {
          await dispatch(
            getOrganizationByIdThunk({ organizationId: organizationId }),
          ).unwrap();
        } else {
          setError(
            t(
              'common:pages.organization_overview.assign_members_to_device.cannot_unassign',
            ),
          );
        }
      } catch (e) {
        Sentry.captureException(e, {
          tags: { page: 'ManageDevicesModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [dispatch, organizationId, t, userId],
  );

  const openAssignMembersStage = useCallback(() => {
    setState({ devices: devicesAssignedToMember, stage: 'devices' });
  }, [devicesAssignedToMember, setState]);

  return (
    <Box
      sx={{
        height: '45vh',
        overflow: 'auto',
        paddingBottom: 5,
        width: '100%',
      }}
    >
      <Box mb={4}>
        <Typography variant="h2" mb={2} textAlign="center">
          {t(
            userRole === 'member'
              ? 'common:pages.organization_overview.assign_devices_to_member.manage_devices'
              : 'common:pages.organization_overview.assign_devices_to_member.assigned_devices',
          )}
        </Typography>
      </Box>

      {userRole === 'member' ? (
        <Box mb={4} width="100%">
          <ListItemButton
            onClick={openAssignMembersStage}
            sx={{
              backgroundColor: theme.vars.palette.grey[100],
              borderRadius: 2,
            }}
            data-testid="assign-members-button"
          >
            <ListItemText
              color="primary"
              primaryTypographyProps={{
                style: {
                  color: theme.vars.palette.primary.dark,
                  fontWeight: 500,
                },
              }}
              primary={t('common:pages.organization_overview.assign_device')}
            />
            <IconButton edge="end" disableRipple>
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          </ListItemButton>
        </Box>
      ) : null}
      <Box>
        <Box mb={3} width="100%">
          <CustomSearchbar
            search={searchString}
            setSearch={setSearchString}
            hasSearched={searchString.length > 0}
            handleSearch={handleSearch}
            handleClearSearch={handleClearSearch}
            testId="manage-devices-search-bar"
          />
        </Box>
        <ErrorText error={error} />
        {filteredDevices.length > 0 ? (
          filteredDevices.map(item => (
            <DeviceList
              key={item.id}
              deviceId={item.id}
              serial={item.serial}
              rightComponent={
                userRole === 'member' ? (
                  <RightComponentWrapper>
                    <IconButton
                      disabled={isLoading}
                      onClick={() => handleUnassignDeviceFromMember(item.id)}
                    >
                      <CustomTrashIcon color="error" />
                    </IconButton>
                  </RightComponentWrapper>
                ) : null
              }
            />
          ))
        ) : (
          <Typography variant="body1" color="grey.600" textAlign="center">
            {t(
              'common:pages.organization_overview.assign_devices_to_member.no_device_found',
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ManageDevices;
